import type { components } from '@connected/api-client/api.generated';
import type { Prettify } from '@connected/api-client/lib/utils';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { Button } from '@connected/ui/components/button';
import { Separator } from '@connected/ui/components/separator';
import { Skeleton } from '@connected/ui/components/skeleton';
import { booleanDefaultValue } from '@connected/ui/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { Breadcrumb } from '@/components/breadcrumb';
import { SubmenuNavigation } from '@/components/submenu-navigation';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/settings/templates/$templateId/',
)({
    component: RouteComponent,
    validateSearch: z.object({
        tab: z.enum(['general', 'texts', 'styles', 'image-quality']).optional().default('general'),
    }),
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(documentTemplateOptions(params));
    },
});

function documentTemplateOptions({ templateId }: { templateId: string }) {
    return api.queryOptions('get', '/api/v2/documents/templates/edit', {
        params: {
            query: {
                DocumentTemplateId: templateId,
            },
        },
    });
}

type Tailwind = {
    [K in keyof components['schemas']['EditDocumentTemplateQueryResultTailwind']]:
        | { fieldKind: 'Text'; value: string | null }
        | { fieldKind: 'WholeNumber'; value: number | null }
        | { fieldKind: 'DecimalNumber'; value: number | null }
        | { fieldKind: 'Color'; value: string | null };
};

type Setting = Prettify<{
    documentTemplateSettingId: string;
    settingKind: string;
    enabled: boolean;
} & (
    | { fieldKind: 'ImageQuality'; value: components['schemas']['ImageQuality'] | null }
    | { fieldKind: 'Toggle'; value: boolean | null }
    | { fieldKind: 'WholeNumber'; value: number | null }
    | { fieldKind: 'DecimalNumber'; value: number | null }
    | { fieldKind: 'Date'; value: Date | null }
    | { fieldKind: 'DateTime'; value: Date | null }
    | { fieldKind: 'DateTimeOffset'; value: Date | null }
    | { fieldKind: 'Text'; value: string | null }
    | { fieldKind: 'Html'; value: string | null }
    | { fieldKind: 'Css'; value: string | null }
    )>;

function RouteComponent() {
    const { t } = useTranslation('dashboard');
    const search = Route.useSearch();
    const params = Route.useParams();
    const query = useQuery(documentTemplateOptions(params));

    return (
        <>
            <Breadcrumb
                showHome={false}
                items={[
                    {
                        name: t('dashboard.settings.reports.title'),
                        to: '/dashboard/organizations/$organizationId/settings/templates',
                        params: {
                            organizationId: params.organizationId,
                        },
                    },
                    {
                        isActive: true,
                        name: query.data?.DocumentTemplate?.Name || '',
                    },
                ]}
            />
            <div className="mb-6">
                <div className="flex max-sm:flex-wrap gap-1 md:gap-4 justify-start">
                    <h1 className="text-2xl font-semibold">
                        {query.data?.DocumentTemplate?.Name || ''}
                    </h1>
                </div>
            </div>
            <SubmenuNavigation
                items={[
                    {
                        label: t('dashboard.settings.reports.tabs.general'),
                        to: '/dashboard/organizations/$organizationId/settings/templates/$templateId',
                        search: {
                            tab: 'general',
                        },
                    },
                    {
                        label: t('dashboard.settings.reports.tabs.texts'),
                        to: '/dashboard/organizations/$organizationId/settings/templates/$templateId',
                        search: {
                            tab: 'texts',
                        },
                    },
                    {
                        label: t('dashboard.settings.reports.tabs.styling'),
                        to: '/dashboard/organizations/$organizationId/settings/templates/$templateId',
                        search: {
                            tab: 'styles',
                        },
                    },
                    {
                        label: t('dashboard.settings.reports.tabs.image-quality'),
                        to: '/dashboard/organizations/$organizationId/settings/templates/$templateId',
                        search: {
                            tab: 'image-quality',
                        },
                    },
                ]}
            />

            <TemplateSettings tab={search.tab} />
        </>
    );
}

function TemplateSettings({ tab }: { tab: 'general' | 'texts' | 'styles' | 'image-quality' }) {
    const params = Route.useParams();
    const query = useQuery(documentTemplateOptions(params));

    const editTemplateMutation = api.useMutation(
        'put',
        '/api/v2/documents/templates/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    documentTemplateOptions(params),
                );
            },
        },
    );

    const disabled = useMemo(() => {
        return query.isPending || editTemplateMutation.isPending;
    }, [query.isPending, editTemplateMutation.isPending]);

    const errors = useMemo(() => {
        return [editTemplateMutation.error?.Message, query.error?.Message].filter(Boolean) as string[];
    }, [query.error, editTemplateMutation.error]);

    const tailwind = useMemo(() => {
        return query.data?.DocumentTemplate?.Tailwind
            ? Object.fromEntries(
                Object.entries(query.data.DocumentTemplate.Tailwind).map(([key, value]) => {
                    const colorFields = [
                        'GradientPrimary',
                        'GradientSecondary',
                        'BackgroundPrimary',
                        'BackgroundSecondary',
                        'BackgroundTertiary',
                        'BackgroundQuaternary',
                        'BackgroundPopover',
                        'BackgroundTab',
                        'TextPrimary',
                        'TextSecondary',
                        'TextTertiary',
                        'TextQuaternary',
                        'IconPrimary',
                        'IconSecondary',
                        'BrandPrimary',
                        'BrandSecondary',
                    ];

                    const fieldKind = (() => {
                        if (colorFields.includes(key)) {
                            return 'Color';
                        }
                        if (key.includes('FontWeight')) {
                            return 'WholeNumber';
                        }
                        if (key.includes('LineHeight')) {
                            return 'DecimalNumber';
                        }

                        return 'Text';
                    })();

                    return [key, { fieldKind, value }];
                }),
            ) as Tailwind
            : {};
    }, [query.data?.DocumentTemplate?.Tailwind]);

    const settings = useMemo(() => {
        return query.data?.DocumentTemplate?.Settings?.map((setting): Setting => {
            const base = {
                documentTemplateSettingId: setting.DocumentTemplateSettingId!,
                settingKind: setting.SettingKind || '',
                fieldKind: setting.FieldKind,
                enabled: setting.Enabled || false,
            };

            switch (setting.FieldKind) {
                case 'ImageQuality':
                    return {
                        ...base,
                        fieldKind: 'ImageQuality',
                        value: setting.ImageQuality ?? null,
                    };
                case 'Toggle':
                    return {
                        ...base,
                        fieldKind: 'Toggle',
                        value: setting.Toggle ?? null,
                    };
                case 'WholeNumber':
                    return {
                        ...base,
                        fieldKind: 'WholeNumber',
                        value: setting.WholeNumber ?? null,
                    };
                case 'DecimalNumber':
                    return {
                        ...base,
                        fieldKind: 'DecimalNumber',
                        value: setting.DecimalNumber ?? null,
                    };
                case 'Date':
                    return {
                        ...base,
                        fieldKind: 'Date',
                        value: setting.Date ? new Date(setting.Date) : null,
                    };
                case 'DateTime':
                    return {
                        ...base,
                        fieldKind: 'DateTime',
                        value: setting.DateTime ? new Date(setting.DateTime) : null,
                    };
                case 'DateTimeOffset':
                    return {
                        ...base,
                        fieldKind: 'DateTimeOffset',
                        value: setting.DateTimeOffset ? new Date(setting.DateTimeOffset) : null,
                    };
                case 'Text':
                    return {
                        ...base,
                        fieldKind: 'Text',
                        value: setting.Text ?? null,
                    };
                case 'Html':
                    return {
                        ...base,
                        fieldKind: 'Html',
                        value: setting.Text ?? null, // no Html property so using Text
                        // value: setting.Text?.length ? setting.Text : (setting.DefaultHtml?.length ? setting.DefaultHtml : null), // Use for future whenever DefaultHtml returns good plain html
                    };
                case 'Css':
                    return {
                        ...base,
                        fieldKind: 'Css',
                        value: setting.Text ?? '',
                    };
                default:
                    throw new Error(`Unknown setting kind: ${setting.FieldKind}`);
            }
        }) || [];
    }, [query.data?.DocumentTemplate?.Settings]);

    const form = useAppForm({
        defaultValues: {
            name: query.data?.DocumentTemplate?.Name || '',
            description: query.data?.DocumentTemplate?.Description || '',
            groupName: query.data?.DocumentTemplate?.GroupName,
            order: query.data?.DocumentTemplate?.Order || 0,
            isDefault: booleanDefaultValue(query.data?.DocumentTemplate?.IsDefault) || false,
            isPublic: booleanDefaultValue(query.data?.DocumentTemplate?.IsPublic) || false,
            enabled: booleanDefaultValue(query.data?.DocumentTemplate?.Enabled) || false,
            tailwind,
            settings,
        },
        onSubmit: async ({ value }) => {
            await editTemplateMutation.mutateAsync({
                body: {
                    DocumentTemplateId: query.data!.DocumentTemplate!.DocumentTemplateId!,
                    Name: value.name,
                    Description: value.description,
                    GroupName: value.groupName,
                    Order: value.order,
                    IsDefault: value.isDefault,
                    IsPublic: value.isPublic,
                    Enabled: value.enabled,
                    Tailwind: Object.fromEntries(Object.entries(value.tailwind).map(([key, value]) => [key, value?.value ?? null])),
                    Settings: value.settings?.map((setting) => {
                        const base = {
                            DocumentTemplateSettingId: setting.documentTemplateSettingId,
                            Enabled: setting.enabled,
                        };
                        switch (setting.fieldKind) {
                            case 'ImageQuality':
                                return {
                                    ...base,
                                    FieldKind: 'ImageQuality',
                                    ImageQuality: setting.value,
                                };
                            case 'Toggle':
                                return {
                                    ...base,
                                    FieldKind: 'Toggle',
                                    Toggle: setting.value,
                                };
                            case 'WholeNumber':
                                return {
                                    ...base,
                                    FieldKind: 'WholeNumber',
                                    WholeNumber: setting.value,
                                };
                            case 'DecimalNumber':
                                return {
                                    ...base,
                                    FieldKind: 'DecimalNumber',
                                    DecimalNumber: setting.value,
                                };
                            case 'Date':
                                return {
                                    ...base,
                                    FieldKind: 'Date',
                                    Date: setting.value ? setting.value.toISOString() : null,
                                };
                            case 'DateTime':
                                return {
                                    ...base,
                                    FieldKind: 'DateTime',
                                    DateTime: setting.value ? setting.value.toISOString() : null,
                                };
                            case 'DateTimeOffset':
                                return {
                                    ...base,
                                    FieldKind: 'DateTimeOffset',
                                    DateTimeOffset: setting.value ? setting.value.toISOString() : null,
                                };
                            case 'Text':
                                return {
                                    ...base,
                                    FieldKind: 'Text',
                                    Text: setting.value,
                                };
                            case 'Html':
                                return {
                                    ...base,
                                    FieldKind: 'Html',
                                    Text: setting.value,
                                };
                            case 'Css':
                                return {
                                    ...base,
                                    FieldKind: 'Css',
                                    Text: setting.value,
                                };
                            default:
                                throw new Error(`Unknown setting kind: unknown`);
                        }
                    }),
                },
            });
            form.reset();
        },
    });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    useEffect(() => {
        form.reset();
    }, [params.templateId, form]);

    if (query.data == null)
        return <p>Template niet gevonden</p>;

    return (
        <>
            <form onSubmit={handleSubmit} className="w-full flex flex-col gap-1">
                <form.AppForm>
                    {tab === 'general' && (
                        <>
                            {query.isPending
                                ? <Skeleton className="h-10" />
                                : (
                                        <form.AppField
                                            name="name"
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.name,
                                            }}
                                        >
                                            {field => (
                                                <div className="flex flex-col">
                                                    <field.Label className="lg:flex-row lg:items-center">
                                                        <field.Label.Title label="Naam" className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                                        <field.Input
                                                            type="text"
                                                            placeholder="Noteer naam"
                                                            disabled={disabled}
                                                            className="w-full lg:w-2/4 2xl:w-2/5"
                                                        />
                                                    </field.Label>
                                                    <div className="flex lg:gap-2">
                                                        <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                        <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                                    </div>
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                            <Separator className="my-5" />
                            {query.isPending
                                ? <Skeleton className="h-10" />
                                : (
                                        <form.AppField
                                            name="description"
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.description,
                                            }}
                                        >
                                            {field => (
                                                <div className="flex flex-col">
                                                    <field.Label className="lg:flex-row lg:items-center">
                                                        <field.Label.Title label="Beschrijving" className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                                        <field.Input
                                                            type="text"
                                                            placeholder="Noteer beschrijving"
                                                            disabled={disabled}
                                                            className="w-full lg:w-2/4 2xl:w-2/5"
                                                        />
                                                    </field.Label>
                                                    <div className="flex lg:gap-2">
                                                        <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                        <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                                    </div>
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                            <Separator className="my-5" />
                            {query.isPending
                                ? <Skeleton className="h-10" />
                                : (
                                        <form.AppField
                                            name="groupName"
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.groupName,
                                            }}
                                        >
                                            {field => (
                                                <div className="flex flex-col">
                                                    <field.Label className="lg:flex-row lg:items-center">
                                                        <field.Label.Title label="Groep" className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                                        <field.Input
                                                            type="text"
                                                            placeholder="Noteer groep"
                                                            disabled={disabled}
                                                            className="w-full lg:w-2/4 2xl:w-2/5"
                                                        />
                                                    </field.Label>
                                                    <div className="flex lg:gap-2">
                                                        <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                        <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                                    </div>
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                            <Separator className="my-5" />
                            {query.isPending
                                ? <Skeleton className="h-10" />
                                : (
                                        <form.AppField
                                            name="order"
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.order,
                                            }}
                                        >
                                            {field => (
                                                <div className="flex flex-col">
                                                    <field.Label className="lg:flex-row lg:items-center">
                                                        <field.Label.Title label="Volgnummer" className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                                        <field.Input
                                                            type="text-number"
                                                            placeholder="Noteer volgnummer"
                                                            disabled={disabled}
                                                            className="w-full lg:w-2/4 2xl:w-2/5"
                                                        />
                                                    </field.Label>
                                                    <div className="flex lg:gap-2">
                                                        <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                        <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                                    </div>
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                            <Separator className="my-5" />
                            {query.isPending
                                ? <Skeleton className="h-8 w-full" />
                                : (
                                        <form.AppField
                                            name="enabled"
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.enabled,
                                            }}
                                        >

                                            {field => (
                                                <div className="flex flex-col">
                                                    <field.Label className="flex-row items-center">
                                                        <field.Label.Title label="Is ingeschakeld?" className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                                        <field.Switch className="w-full lg:w-2/4 2xl:w-2/5" />
                                                    </field.Label>
                                                    <div className="flex lg:gap-2">
                                                        <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                        <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                                    </div>
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                            <Separator className="my-5" />
                            {query.isPending
                                ? <Skeleton className="h-8 w-full" />
                                : (
                                        <form.AppField
                                            name="isDefault"
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.isDefault,
                                            }}
                                        >

                                            {field => (
                                                <div className="flex flex-col">
                                                    <field.Label className="flex-row items-center">
                                                        <field.Label.Title label="Is standaard?" className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                                        <field.Switch className="w-full lg:w-2/4 2xl:w-2/5" />
                                                    </field.Label>
                                                    <div className="flex lg:gap-2">
                                                        <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                        <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                                    </div>
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                            <Separator className="my-5" />
                            {query.isPending
                                ? <Skeleton className="h-8 w-full" />
                                : (
                                        <form.AppField
                                            name="isPublic"
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.isPublic,
                                            }}
                                        >

                                            {field => (
                                                <div className="flex flex-col">
                                                    <field.Label className="flex-row items-center">
                                                        <field.Label.Title label="Is openbaar?" className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                                        <field.Switch className="w-full lg:w-2/4 2xl:w-2/5" />
                                                    </field.Label>
                                                    <div className="flex lg:gap-2">
                                                        <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                        <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                                    </div>
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                            <Separator className="my-5" />
                        </>
                    )}

                    {/* Filter tailwind by key including/excluding Gradient */}
                    {['general', 'styles'].includes(tab) && query.isPending
                        ? <Skeleton className="h-8 w-full" />
                        : (
                                <form.AppField name="tailwind">
                                    {field => Object.entries(field.form.state.values.tailwind)
                                        .map(([settingKey, setting]) => {
                                            if (
                                                (tab === 'general' && settingKey.includes('Gradient'))
                                                || (tab === 'styles' && !settingKey.includes('Gradient'))
                                            ) {
                                                return (
                                                    <Fragment key={settingKey}>
                                                        <TailwindItemField
                                                            settingKey={settingKey as keyof Tailwind}
                                                            setting={setting}
                                                            // TODO: solve this with withform or something
                                                            // @ts-expect-error too time consuming to type this
                                                            form={form}
                                                            disabled={disabled}
                                                        />
                                                        <Separator className="my-5" />
                                                    </Fragment>
                                                );
                                            }

                                            return null;
                                        })}
                                </form.AppField>
                            )}

                    {/* Filter settings by ImageQuality and Html fieldKinds */}
                    {['general', 'texts', 'image-quality', 'styles'].includes(tab) && query.isPending
                        ? <Skeleton className="h-8 w-full" />
                        : (
                                <form.AppField name="settings">
                                    {field => field.form.state.values.settings
                                        .map((setting, settingIndex) => {
                                            if (
                                                (tab === 'general' && !['ImageQuality', 'Html'].includes(setting.fieldKind))
                                                || (tab === 'image-quality' && setting.fieldKind === 'ImageQuality')
                                                || (tab === 'texts' && setting.fieldKind === 'Html')
                                                || (tab === 'styles' && setting.fieldKind === 'Css')
                                            ) {
                                                return (
                                                    <Fragment key={setting.documentTemplateSettingId}>
                                                        <SettingItemField
                                                            settingIndex={settingIndex}
                                                            setting={setting}
                                                            // TODO: solve this with withform or something
                                                            // @ts-expect-error too time consuming to type this
                                                            form={form}
                                                            disabled={disabled}
                                                        />
                                                        <Separator className="my-5" />
                                                    </Fragment>
                                                );
                                            }

                                            return null;
                                        })}
                                </form.AppField>
                            )}

                    <div className="flex w-full gap-4">
                        <Button className="w-full" variant="default" onClick={form.handleSubmit} disabled={disabled}>
                            Opslaan
                        </Button>
                    </div>
                    <div className="flex flex-col gap-4">
                        {errors.length > 0 && (
                            <div className="text-red-500">
                                {errors.map(error => (
                                    <div key={error}>{error}</div>
                                ))}
                            </div>
                        )}
                    </div>
                </form.AppForm>
            </form>
        </>
    );
}

export function TailwindItemField({
    settingKey,
    setting,
    form,
    disabled,
}: {
    settingKey: keyof Tailwind;
    setting: Tailwind[keyof Tailwind];
    form: ReturnType<typeof useAppForm>;
    disabled?: boolean;
}) {
    const { t } = useTranslation('dashboard');

    switch (setting?.fieldKind) {
        case 'Text':
            return (
                <form.AppField
                    key={settingKey}
                    name={`tailwind.${settingKey}.value`}
                    validators={{
                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind[settingKey as keyof typeof validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind],
                    }}
                >
                    {field => (
                        <div className="flex flex-col">
                            <field.Label className="lg:flex-row lg:items-center">
                                <field.Label.Title label={t(`dashboard.pdf-templates.tailwind.${settingKey}`)} className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                <field.Input
                                    type="text"
                                    placeholder={`${t(`dashboard.pdf-templates.placeholder-prefix-enter`)} ${t(`dashboard.pdf-templates.tailwind.${settingKey}`).toLowerCase()}`}
                                    disabled={disabled}
                                    className="w-full lg:w-2/4 2xl:w-2/5"
                                />
                            </field.Label>
                            <div className="flex lg:gap-2">
                                <div className="lg:w-1/4 2xl:w-1/5"></div>
                                <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                            </div>
                        </div>
                    )}
                </form.AppField>
            );
        case 'WholeNumber':
        case 'DecimalNumber':
            return (
                <form.AppField
                    key={settingKey}
                    name={`tailwind.${settingKey}.value`}
                    validators={{
                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind[settingKey as keyof typeof validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind],
                    }}
                >
                    {field => (
                        <div className="flex flex-col">
                            <field.Label className="lg:flex-row lg:items-center">
                                <field.Label.Title label={t(`dashboard.pdf-templates.tailwind.${settingKey}`)} className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                <field.Input
                                    type="number"
                                    placeholder={`${t(`dashboard.pdf-templates.placeholder-prefix-enter`)} ${t(`dashboard.pdf-templates.tailwind.${settingKey}`).toLowerCase()}`}
                                    disabled={disabled}
                                    step={setting.fieldKind === 'WholeNumber' ? '1' : '.01'}
                                    className="w-full lg:w-2/4 2xl:w-2/5"
                                />
                            </field.Label>
                            <div className="flex lg:gap-2">
                                <div className="lg:w-1/4 2xl:w-1/5"></div>
                                <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                            </div>
                        </div>
                    )}
                </form.AppField>
            );
        case 'Color':
            return (
                <form.AppField
                    key={settingKey}
                    name={`tailwind.${settingKey}.value`}
                    validators={{
                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind[settingKey as keyof typeof validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind],
                    }}
                >
                    {field => (
                        <div className="flex flex-col">
                            <field.Label className="lg:flex-row lg:items-center">
                                <field.Label.Title label={t(`dashboard.pdf-templates.tailwind.${settingKey}`)} className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                <field.Popover>
                                    <div className="w-full lg:w-2/4 2xl:w-2/5 flex gap-2">
                                        <field.Popover.Trigger asChild>
                                            <field.GradientPicker.Trigger />
                                        </field.Popover.Trigger>
                                        <field.GradientPicker.Copy />
                                        <field.GradientPicker.Paste />
                                    </div>
                                    <field.Popover.Content>
                                        <field.GradientPicker
                                            className="mt-1"
                                            disabled={disabled}
                                        />
                                    </field.Popover.Content>
                                </field.Popover>
                            </field.Label>
                            <div className="flex lg:gap-2">
                                <div className="lg:w-1/4 2xl:w-1/5"></div>
                                <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                            </div>
                        </div>
                    )}
                </form.AppField>
            );
        default:
            return null;
    }
}

export type SettingItemFieldProps = {
    settingIndex: number;
    setting: Setting;
    form: ReturnType<typeof useAppForm>;
    disabled?: boolean;
};

export function SettingItemField({ settingIndex, setting, form, disabled }: SettingItemFieldProps) {
    const { t } = useTranslation('dashboard');
    const enumValues = ['Original', 'OriginalWebp', 'ExtraLarge', 'Large', 'Medium', 'Small', 'ExtraSmall'];

    switch (setting.fieldKind) {
        case 'ImageQuality':
            return (
                <div className="flex flex-col gap-2">
                    <form.AppField
                        name={`settings[${settingIndex}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {field => (
                            <div className="flex flex-col">
                                <field.Label className="flex-row items-center">
                                    <field.Label.Title label={t(`dashboard.pdf-templates.settings.${setting.settingKind}`)} className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                    <field.Switch className="w-full lg:w-2/4 2xl:w-2/5" disabled={disabled} />
                                </field.Label>

                                {/* TODO: solve this and replace Subscribe with ShowIf */}
                                {/* @ts-expect-error values unknown */}
                                <form.Subscribe selector={field => [field.values.settings[settingIndex].enabled]}>
                                    {([Enabled]) => Enabled && (
                                        <form.AppField
                                            name={`settings.${settingIndex}.value`}
                                        >
                                            {field => (
                                                <div className="flex lg:gap-2">
                                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                    <field.Label className="w-full lg:w-2/4 2xl:w-2/5 mt-2">
                                                        <field.Popover closeOnChange={true}>
                                                            <field.RadioGroup options={enumValues.map(enumValue => ({ label: enumValue, value: enumValue }))}>
                                                                <field.Popover.Trigger asChild>
                                                                    <field.RadioGroup.Trigger placeholder={`${t(`dashboard.pdf-templates.placeholder-prefix-select`)}`} />
                                                                </field.Popover.Trigger>
                                                                <field.Popover.Content matchTriggerWidth={true}>
                                                                    <field.RadioGroup.Items />
                                                                </field.Popover.Content>
                                                            </field.RadioGroup>
                                                        </field.Popover>
                                                    </field.Label>
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                                </form.Subscribe>

                                <div className="flex lg:gap-2">
                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                    <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                </div>
                            </div>
                        )}
                    </form.AppField>
                </div>
            );
        case 'Toggle':
            return (
                <form.AppField
                    name={`settings[${settingIndex}].value`}
                    validators={{
                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.toggle,
                    }}
                >
                    {field => (
                        <div className="flex-col gap-2">
                            <field.Label className="flex-row items-center">
                                <field.Label.Title label={t(`dashboard.pdf-templates.settings.${setting.settingKind}`)} className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                <field.Switch className="w-full lg:w-2/4 2xl:w-2/5" />
                            </field.Label>
                            <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                        </div>
                    )}
                </form.AppField>
            );
        case 'WholeNumber':
        case 'DecimalNumber':
            return (
                <div className="flex flex-col gap-2">
                    <form.AppField
                        name={`settings[${settingIndex}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {field => (
                            <div className="flex flex-col">
                                <field.Label className="flex-row items-center">
                                    <field.Label.Title label={t(`dashboard.pdf-templates.settings.${setting.settingKind}`)} className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                    <field.Switch className="w-full lg:w-2/4 2xl:w-2/5" disabled={disabled} />
                                </field.Label>

                                {/* TODO: solve this and replace Subscribe with ShowIf */}
                                {/* @ts-expect-error values unknown */}
                                <form.Subscribe selector={field => [field.values.settings[settingIndex].enabled]}>
                                    {([Enabled]) => Enabled && (
                                        <form.AppField
                                            key={settingIndex}
                                            name={`settings.${settingIndex}.value`}
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.decimalNumber,
                                            }}
                                        >
                                            {field => (
                                                <div className="flex lg:gap-2">
                                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                    <field.Input
                                                        type="number"
                                                        placeholder={`${t(`dashboard.pdf-templates.placeholder-prefix-enter`)} ${t(`dashboard.pdf-templates.settings.${setting.settingKind}`).toLowerCase()}`}
                                                        disabled={disabled}
                                                        step={setting.fieldKind === 'WholeNumber' ? '1' : '.01'}
                                                        className="w-full lg:w-2/4 2xl:w-2/5 mt-2"
                                                    />
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                                </form.Subscribe>

                                <div className="flex lg:gap-2">
                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                    <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                </div>
                            </div>
                        )}
                    </form.AppField>
                </div>
            );
        case 'Date':
        case 'DateTime':
        case 'DateTimeOffset':
            return (
                <div className="flex flex-col gap-2">
                    <form.AppField
                        name={`settings[${settingIndex}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {field => (
                            <div className="flex flex-col">
                                <field.Label className="flex-row items-center">
                                    <field.Label.Title label={t(`dashboard.pdf-templates.settings.${setting.settingKind}`)} className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                    <field.Switch className="w-full lg:w-2/4 2xl:w-2/5" disabled={disabled} />
                                </field.Label>

                                {/* TODO: solve this and replace Subscribe with ShowIf */}
                                {/* @ts-expect-error values unknown */}
                                <form.Subscribe selector={field => [field.values.settings[settingIndex].enabled]}>
                                    {([Enabled]) => Enabled && (
                                        <form.AppField
                                            name={`settings.${settingIndex}.value`}
                                        >
                                            {field => (
                                                <div className="flex lg:gap-2">
                                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                    <field.Label className="w-full lg:w-2/4 2xl:w-2/5 mt-2">
                                                        <field.Label.Title label="" />
                                                        <field.Popover closeOnChange={true}>
                                                            <field.Popover.Trigger asChild>
                                                                <field.DatePicker.Trigger
                                                                    placeholder={`${t(`dashboard.pdf-templates.placeholder-prefix-select`)} ${t(`dashboard.pdf-templates.settings.${setting.settingKind}`).toLowerCase()}`}
                                                                />
                                                            </field.Popover.Trigger>
                                                            <field.Popover.Content matchTriggerWidth={true}>
                                                                <field.DatePicker disabled={disabled} />
                                                            </field.Popover.Content>
                                                        </field.Popover>
                                                        <field.ValidationError />
                                                    </field.Label>
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                                </form.Subscribe>

                                <div className="flex lg:gap-2">
                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                    <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                </div>
                            </div>
                        )}
                    </form.AppField>
                </div>
            );
        case 'Html':
        case 'Css':
            return (
                <div className="flex flex-col gap-2">
                    <form.AppField
                        name={`settings[${settingIndex}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {field => (
                            <div className="flex flex-col">
                                <field.Label className="flex-row items-center">
                                    <field.Label.Title label={t(`dashboard.pdf-templates.settings.${setting.settingKind}`)} className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                    <field.Switch className="w-full lg:w-2/4 2xl:w-2/5" disabled={disabled} />
                                </field.Label>

                                {/* TODO: solve this and replace Subscribe with ShowIf */}
                                {/* @ts-expect-error values unknown */}
                                <form.Subscribe selector={field => [field.values.settings[settingIndex].enabled]}>
                                    {([Enabled]) => Enabled && (
                                        <form.AppField
                                            key={settingIndex}
                                            name={`settings.${settingIndex}.value`}
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.text,
                                            }}
                                        >
                                            {field => (
                                                <div className="flex lg:gap-2">
                                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                    <field.Textarea
                                                        placeholder={`${t(`dashboard.pdf-templates.placeholder-prefix-enter`)} ${t(`dashboard.pdf-templates.settings.${setting.settingKind}`).toLowerCase()}`}
                                                        disabled={disabled}
                                                        className="w-full lg:w-2/4 2xl:w-2/5 mt-2"
                                                    />
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                                </form.Subscribe>

                                <div className="flex lg:gap-2">
                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                    <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                </div>
                            </div>
                        )}
                    </form.AppField>
                </div>
            );

        case 'Text':
            return (
                <div className="flex flex-col gap-2">
                    <form.AppField
                        name={`settings[${settingIndex}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {field => (
                            <div className="flex flex-col">
                                <field.Label className="flex-row items-center">
                                    <field.Label.Title label={t(`dashboard.pdf-templates.settings.${setting.settingKind}`)} className="w-1/2 lg:w-1/4 2xl:w-1/5" />
                                    <field.Switch className="w-full lg:w-2/4 2xl:w-2/5" disabled={disabled} />
                                </field.Label>

                                {/* TODO: solve this and replace Subscribe with ShowIf */}
                                {/* @ts-expect-error values unknown */}
                                <form.Subscribe selector={field => [field.values.settings[settingIndex].enabled]}>
                                    {([Enabled]) => Enabled && (
                                        <form.AppField
                                            key={settingIndex}
                                            name={`settings.${settingIndex}.value`}
                                            validators={{
                                                onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.text,
                                            }}
                                        >
                                            {field => (
                                                <div className="flex lg:gap-2">
                                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                                    <field.Input
                                                        type="text"
                                                        placeholder={`${t(`dashboard.pdf-templates.placeholder-prefix-enter`)} ${t(`dashboard.pdf-templates.settings.${setting.settingKind}`).toLowerCase()}`}
                                                        disabled={disabled}
                                                        className="w-full lg:w-2/4 2xl:w-2/5 mt-2"
                                                    />
                                                </div>
                                            )}
                                        </form.AppField>
                                    )}
                                </form.Subscribe>

                                <div className="flex lg:gap-2">
                                    <div className="lg:w-1/4 2xl:w-1/5"></div>
                                    <field.ValidationError className="lg:w-1/4 2xl:w-2/5 mt-2" />
                                </div>
                            </div>
                        )}
                    </form.AppField>
                </div>
            );
        default:
            return null;
    }
}
